import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import Modal from '../../components/common/Modal';
import { useSocketEvent } from '../../hooks/useSocket';
import { isSocketConnected } from '../../services/socket';

const PAYBILL_NUMBER = '4039241';

const getSourceBadge = (source) => {
  const map = {
    'c2b-auto': { label: '📱 Auto M-Pesa', color: '#059669', bg: '#ecfdf5', border: '#10b981' },
    'counter': { label: '🏢 Counter', color: '#2563eb', bg: '#eff6ff', border: '#3b82f6' },
    'manual': { label: '📋 Manual', color: '#d97706', bg: '#fffbeb', border: '#f59e0b' },
    'bank-transfer': { label: '🏦 Bank', color: '#7c3aed', bg: '#f5f3ff', border: '#8b5cf6' },
  };
  const s = map[source] || map['manual'];
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 12,
      fontWeight: 600, color: s.color, background: s.bg, border: `1px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
};

export default function RepaymentHistory() {
  const [repayments, setRepayments] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({ source: '', search: '' });
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedRepayment, setSelectedRepayment] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [liveFlash, setLiveFlash] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // FETCH — called on mount, filter/page change, AND socket event
  // ═══════════════════════════════════════════════════════════
  const fetchRepayments = useCallback(async (silent = false) => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.source) params.append('paymentSource', filters.source);
      const res = await api.get(`/repayments?${params}`);
      setRepayments(res.data.data.repayments);
      setPagination(res.data.data.pagination);
      if (!silent) setLastUpdate(Date.now());
    } catch (err) {
      console.error('Failed to fetch repayments:', err);
    }
  }, [page, filters.source]);

  useEffect(() => {
    fetchRepayments();
  }, [fetchRepayments]);

  // ═══════════════════════════════════════════════════════════
  // AUTO-UPDATE: Socket listeners fire when C2B/counter/manual
  // payments arrive — no button clicks or manual refresh needed
  // ═══════════════════════════════════════════════════════════
  const handleNewRepayment = useCallback(() => {
    console.log('🔴 RepaymentHistory: Auto-refresh triggered by socket event');
    setLiveFlash(true);
    setTimeout(() => setLiveFlash(false), 3000);
    fetchRepayments(true);
  }, [fetchRepayments]);

  useSocketEvent('repayment:recorded', handleNewRepayment);
  useSocketEvent('c2b:payment', handleNewRepayment);
  useSocketEvent('counter:payment', handleNewRepayment);

  // ═══════════════════════════════════════════════════════════
  // FALLBACK: 30s safety poll in case socket drops
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRepayments(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchRepayments]);

  // ═══════════════════════════════════════════════════════════
  // VIEW RECEIPT — fetches FULL loan details from the same
  // API endpoint the loan detail page uses, so totalRepayable
  // and totalPaid match exactly what the detail page shows.
  // ═══════════════════════════════════════════════════════════
  const handleViewReceipt = async (repayment) => {
    setSelectedRepayment(repayment);
    setReceiptLoading(true);
    setShowReceiptModal(true);

    if (repayment.loan?._id) {
      try {
        const res = await api.get(`/loans/${repayment.loan._id}`);
        const fullLoan = res.data.data;
        // Merge full loan data into the repayment's loan object
        // This brings in totalRepayable, totalPaid, etc.
        setSelectedRepayment(prev =>
          prev ? { ...prev, loan: { ...prev.loan, ...fullLoan } } : null
        );
      } catch (err) {
        console.error('Failed to fetch full loan details for receipt:', err);
      }
    }

    setReceiptLoading(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h3>Repayment History</h3>

        {/* Live Auto-Update Indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 20,
            background: liveFlash ? '#ecfdf5' : '#f1f5f9',
            border: `1px solid ${liveFlash ? '#10b981' : '#e2e8f0'}`,
            transition: 'all 0.3s ease',
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: liveFlash ? '#10b981' : (isSocketConnected() ? '#3b82f6' : '#94a3b8'),
              animation: liveFlash ? 'livePulse 0.6s ease-in-out 3' : (isSocketConnected() ? 'liveBlink 2s infinite' : 'none'),
            }} />
            <span style={{
              color: liveFlash ? '#059669' : '#64748b',
              fontWeight: 600,
            }}>
              {liveFlash ? '🔴 LIVE UPDATE' : (isSocketConnected() ? 'Auto-Updating' : 'Polling')}
            </span>
          </div>
          <span style={{ color: '#94a3b8', fontSize: 11 }}>
            Last: {new Date(lastUpdate).toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-2" style={{ alignItems: 'center' }}>
        <select
          className="form-control"
          style={{ maxWidth: '200px' }}
          value={filters.source}
          onChange={e => { setFilters({ ...filters, source: e.target.value }); setPage(1); }}
        >
          <option value="">All Sources</option>
          <option value="c2b-auto">📱 Auto M-Pesa</option>
          <option value="counter">🏢 Counter</option>
          <option value="manual">📋 Manual</option>
          <option value="bank-transfer">🏦 Bank</option>
        </select>
        {filters.source && (
          <button className="btn btn-sm btn-outline" onClick={() => { setFilters({ source: '', search: '' }); setPage(1); }}>Clear</button>
        )}
      </div>

      <div className="card">
        <div className="table-container">
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Source</th>
                <th>Loan</th>
                <th>Client</th>
                <th>Amount</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Penalty</th>
                <th>Method</th>
                <th>Collected By</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {repayments.length === 0 ? (
                <tr><td colSpan="11" className="text-center p-4 text-muted">No repayments found.</td></tr>
              ) : repayments.map(row => (
                <tr key={row._id}>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => handleViewReceipt(row)}>📄</button>
                  </td>
                  <td>{getSourceBadge(row.paymentSource)}</td>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.loan?.loanNumber}</td>
                  <td>
                    <div>{row.client?.firstName} {row.client?.lastName}</div>
                    <div className="text-xs text-muted">{row.client?.nationalId}</div>
                  </td>
                  <td style={{ fontWeight: 700 }}>{formatCurrency(row.amount)}</td>
                  <td style={{ color: '#10b981' }}>{formatCurrency(row.principalAmount || 0)}</td>
                  <td style={{ color: '#f59e0b' }}>{formatCurrency(row.interestAmount || 0)}</td>
                  <td style={{ color: (row.penaltyAmount || 0) > 0 ? '#dc2626' : '#94a3b8' }}>{formatCurrency(row.penaltyAmount || 0)}</td>
                  <td>{row.paymentMethod?.replace('-', ' ').toUpperCase()}</td>
                  <td>
                    {row.collectedBy
                      ? `${row.collectedBy.firstName || ''} ${row.collectedBy.lastName || ''}`
                      : <span style={{ color: '#059669', fontWeight: 600, fontSize: 11 }}>SYSTEM AUTO</span>
                    }
                  </td>
                  <td>{formatDate(row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination && (
          <div className="flex justify-between items-center text-sm text-muted mt-2 p-2" style={{ borderTop: '1px solid #eee' }}>
            <span>Page {page} of {pagination.pages} (Total: {pagination.total})</span>
            <div className="flex gap-1">
              <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <button className="btn btn-sm btn-outline" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>

      {/* RECEIPT MODAL */}
      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)} title="Payment Receipt" size="500px">
        {receiptLoading ? (
          <div className="text-center p-5">
            <span style={{ animation: 'liveBlink 1s infinite', display: 'inline-block', fontSize: '24px' }}>⏳</span>
            <div className="text-muted mt-2">Loading receipt details...</div>
          </div>
        ) : selectedRepayment && (() => {
          const loan = selectedRepayment.loan || {};

          // Amount at maturity: Principal + Interest (approved maturity amount)
          const amountAtMaturity = loan.totalRepayable ||
            (loan.principalAmount || 0) + (loan.totalInterest || 0);

          // Remaining at Maturity: matches the loan detail page exactly
          // totalRepayable - totalPaid = how much of the original P+I total is still owed
          const remainingAtMaturity = loan.remainingAtMaturity ??
            Math.max(0, amountAtMaturity - (loan.totalPaid || 0));

          const isCleared = remainingAtMaturity <= 0;

          return (
            <div id="printable-receipt" style={{ fontFamily: 'Arial, sans-serif', padding: 20, border: '1px solid #ddd', background: '#fff' }}>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: 20, borderBottom: '2px solid #333', paddingBottom: 15 }}>
                <h2 style={{ margin: 0, color: '#1e3a5f', fontSize: 24 }}>FlexBoost Limited</h2>
                <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: 14 }}>Loan Management System</p>
              </div>

              {/* Source Badge */}
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                {getSourceBadge(selectedRepayment.paymentSource)}
              </div>

              {/* Receipt Details */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                  <span><strong>Receipt No:</strong> {selectedRepayment.receiptNumber || selectedRepayment.paymentReference}</span>
                  <span><strong>Date:</strong> {formatDate(selectedRepayment.createdAt)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                  <span><strong>Payment Mode:</strong> {selectedRepayment.paymentMethod?.replace('-', ' ').toUpperCase()}</span>
                  <span><strong>Trans ID:</strong> {selectedRepayment.paymentReference}</span>
                </div>
              </div>

              {/* Client & Loan Info */}
              <div style={{ background: '#f9fafb', padding: 15, borderRadius: 8, marginBottom: 20, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 10, color: '#333' }}>Payment Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 14 }}>
                  <div>
                    <div style={{ color: '#666', fontSize: 12 }}>Client Name</div>
                    <div>{selectedRepayment.client?.firstName} {selectedRepayment.client?.lastName}</div>
                    <div style={{ color: '#666', fontSize: 12, marginTop: 6 }}>Client ID</div>
                    <div style={{ fontFamily: 'monospace' }}>{selectedRepayment.client?.nationalId || selectedRepayment.client?.clientNumber}</div>
                  </div>
                  <div>
                    <div style={{ color: '#666', fontSize: 12 }}>Loan Number</div>
                    <div style={{ fontWeight: 'bold', fontSize: 16 }}>{loan.loanNumber}</div>
                    <div style={{ color: '#666', fontSize: 12, marginTop: 6 }}>Collected By</div>
                    <div>{selectedRepayment.collectedBy
                      ? `${selectedRepayment.collectedBy.firstName} ${selectedRepayment.collectedBy.lastName}`
                      : 'System (Auto M-Pesa)'
                    }</div>
                  </div>
                </div>
              </div>

              {/* Financial Breakdown */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: 10, color: '#374151', fontSize: 12 }}>ITEM</th>
                    <th style={{ textAlign: 'right', padding: 10, color: '#374151', fontSize: 12 }}>AMOUNT (KES)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}>Principal</td>
                    <td style={{ textAlign: 'right', padding: 10, borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{formatCurrency(selectedRepayment.principalAmount)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}>Interest</td>
                    <td style={{ textAlign: 'right', padding: 10, borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{formatCurrency(selectedRepayment.interestAmount)}</td>
                  </tr>
                  {(selectedRepayment.penaltyAmount || 0) > 0 && (
                    <tr>
                      <td style={{ padding: 10, borderBottom: '1px solid #eee' }}>Penalty</td>
                      <td style={{ textAlign: 'right', padding: 10, borderBottom: '1px solid #eee', fontWeight: 'bold', color: '#ef4444' }}>{formatCurrency(selectedRepayment.penaltyAmount)}</td>
                    </tr>
                  )}
                  <tr style={{ background: '#f0fdf4' }}>
                    <td style={{ padding: 10, fontWeight: 'bold' }}>TOTAL PAID</td>
                    <td style={{ textAlign: 'right', padding: 10, fontWeight: 'bold', fontSize: 16, color: '#059669' }}>{formatCurrency(selectedRepayment.amount)}</td>
                  </tr>
                  <tr style={{ background: '#eff6ff' }}>
                    <td style={{ padding: 10, fontWeight: 'bold', fontSize: 12, color: '#1e40af' }}>LOAN AMOUNT AT MATURITY (P + I)</td>
                    <td style={{ textAlign: 'right', padding: 10, fontWeight: 'bold', fontSize: 16, color: '#1e40af' }}>
                      {formatCurrency(amountAtMaturity)}
                    </td>
                  </tr>
                  <tr style={{ background: isCleared ? '#ecfdf5' : '#faf5ff' }}>
                    <td style={{ padding: 10, fontWeight: 'bold' }}>ACTIVE LOAN BALANCE</td>
                    <td style={{ textAlign: 'right', padding: 10, fontWeight: 'bold', fontSize: 16, color: isCleared ? '#059669' : '#7c3aed' }}>
                      {formatCurrency(remainingAtMaturity)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Loan Cleared Notice */}
              {isCleared && (
                <div style={{
                  background: '#ecfdf5', border: '2px solid #10b981', borderRadius: 8,
                  padding: 14, textAlign: 'center', marginBottom: 16,
                }}>
                  <div style={{ fontSize: 20 }}>🎉</div>
                  <div style={{ fontWeight: 700, color: '#065f46', fontSize: 15 }}>LOAN FULLY CLEARED</div>
                  <div style={{ fontSize: 13, color: '#047857', marginTop: 4 }}>
                    Client is now eligible to apply for a new loan.
                  </div>
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: 30, textAlign: 'center', borderTop: '1px dashed #ccc', paddingTop: 15 }}>
                <p style={{ margin: 0, color: '#666', fontSize: 12 }}>Thank you for your business!</p>
                <p style={{ margin: '5px 0 0 0', color: '#999', fontSize: 11 }}>This is a computer generated receipt. No signature required.</p>
                {selectedRepayment.paymentSource === 'c2b-auto' && (
                  <p style={{ margin: '5px 0 0 0', color: '#059669', fontSize: 11 }}>
                    Payment received via M-Pesa Paybill {PAYBILL_NUMBER} — Auto-processed
                  </p>
                )}
              </div>

              {/* Print Button */}
              <div style={{ marginTop: 20, textAlign: 'center' }} className="no-print">
                <button
                  onClick={() => window.print()}
                  className="btn btn-primary"
                  style={{ padding: '10px 30px', fontSize: 16 }}
                >
                  🖨️ Print Receipt
                </button>
              </div>
            </div>
          );
        })()}
        <style>{`
          @media print {
            .no-print { display: none !important; }
            #printable-receipt { box-shadow: none; border: 1px solid #000; width: 100%; }
          }
          @keyframes livePulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.8); opacity: 0.4; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes liveBlink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}</style>
      </Modal>

      <style>{`
        @keyframes livePulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.8); opacity: 0.4; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes liveBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}